import { GoogleMap, Marker } from '@react-google-maps/api';
import "./Maps.css";
import { useState, useEffect, useRef } from "react";
import servConsultaAcesso from "../../services/login/servConsultaAcesso";
import { Snackbar, Alert, FormControl, InputLabel, Tooltip } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { HelperText } from "../Errors/HelperText";
import { WhiteInput } from "../Inputs/InputText";
import { BlueHoverButton } from "../Inputs/BlueHoverButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { yellow } from '@mui/material/colors';

const Maps = ({ codigoUc, onChangeCoordinates, hiddenEnderecos, coordenadaSalva, updateKey, disabled, hiddenCamposNaoAutorizados }) => {
  const { control, setValue, formState: { errors } } = useFormContext();
  const [errorMessage, setErrorMessage] = useState("");
  const [coordenadasLatLong, setCoordenadasLatLong] = useState({ lat: -28.67, lng: -49.37 });
  const [openError, setOpenError] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(coordenadasLatLong);
  const [isMarkerLoaded, setIsMarkerLoaded] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [searchAddress, setSearchAddress] = useState("");

  const containerStyle = {
    width: isFullScreen ? '53vw' : '380px',
    height: isFullScreen ? '380px' : '300px',
    borderRadius: '10px',
  };

  const handleCloseError = (_, reason) => {
    if (reason === 'clickaway') return;
    setOpenError(false);
  };

  const formatarCoordenadasGMS = (coord, isLatitude = false) => {
      const absCoord = Math.abs(coord);
      const hemisferio = isLatitude
        ? coord >= 0 ? "N" : "S"
        : coord >= 0 ? "E" : "W";
      
      const graus = Math.floor(absCoord);
      const minutosDecimais = (absCoord - graus) * 60;
      const minutos = Math.floor(minutosDecimais);
      const segundos = (minutosDecimais - minutos) * 60;
      
      // Arredonda para 2 casas decimais e evita 60.00 segundos
      let segundosArredondados = Math.round(segundos * 100) / 100;
      if (segundosArredondados >= 60) {
          segundosArredondados = 59.99;
      }
      
      return `${graus}° ${minutos}' ${segundosArredondados.toFixed(2)}" ${hemisferio}`;
  };

  const verificarCoordenadasUc = async (codigoUc) => {
    if (!codigoUc) {
      recuperarCidadePadrao();
    } else {
      obterCoordenadasLatLong(codigoUc);
    }
  }

  const recuperarCidadePadrao = async () => {
    try {
      const response = await servConsultaAcesso.RecuperarCidadePadrao();

      if (response?.Success) {
        const data = {
          lat: response.Content?.Latitude || -23.550520,
          lng: response.Content?.Longitude || -46.633309,
        };
        setCoordenadasLatLong(data);
        setMarkerPosition(data);
        setValue("CoordenadaX", data.lng);
        setValue("CoordenadaY", data.lat);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.Message || "Erro ao obter coordenadas");
      setOpenError(true);
    }
  }

  const obterCoordenadasLatLong = async (codigoUc) => {
    try {
      const response = await servConsultaAcesso.ObterCoordenadasUtmParaGraus(codigoUc);
      if (response?.Success) {
        const data = {
          lat: response.Content?.CoordenadaY,
          lng: response.Content?.CoordenadaX
        };

        const defaultCoordenadas = {
          lat: -23.550520,
          lng: -46.633309,
        };

        const validarData = {
          lat: data.lat && data.lat !== 0 && data.lat !== -90 ? data.lat : defaultCoordenadas.lat,
          lng: data.lng && data.lng !== 0 ? data.lng : defaultCoordenadas.lng,
        };

        setCoordenadasLatLong(validarData);
        setMarkerPosition(validarData);
        setValue("CoordenadaX", validarData.lng);
        setValue("CoordenadaY", validarData.lat);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.Message || "Erro ao obter coordenadas");
      setOpenError(true);
    }
  };

  const handleMapClick = (e) => {
    const { latLng } = e;
    const lat = latLng.lat();
    const lng = latLng.lng();
    setMarkerPosition({ lat, lng });
    setValue("CoordenadaX", lng);
    setValue("CoordenadaY", lat);
  };

  const handleSearch = async () => {
    if (!searchAddress.trim()) {
      setErrorMessage("Por favor, insira um endereço para buscar.");
      setOpenError(true);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: searchAddress, region: "BR" }, (results, status) => {
      if (status === "OK") {
        const location = results[0].geometry.location;
        const newPosition = { lat: location.lat(), lng: location.lng() };

        setMarkerPosition(newPosition);
        setCoordenadasLatLong(newPosition);
        setValue("CoordenadaX", newPosition.lng);
        setValue("CoordenadaY", newPosition.lat);
      } else {
        setErrorMessage(`Erro ao buscar endereço`);
        setOpenError(true);
      }
    });
  };

  const handleCoordinateChange = (field, value) => {
    const updatedValue = parseFloat(value);
    if (!isNaN(updatedValue)) {
      setMarkerPosition((prev) => {
        const updatedPosition = { ...prev, [field]: updatedValue };
        setCoordenadasLatLong(updatedPosition);

        const formattedValue = formatarCoordenadasGMS(updatedValue);
        setValue(field === "lng" ? "CoordenadaX" : "CoordenadaY", formattedValue);

        return updatedPosition;
      });
    }
  };

  useEffect(() => {
    setIsFullScreen(hiddenEnderecos);
  }, [hiddenEnderecos]);

  useEffect(() => {
    if(!coordenadaSalva){
      verificarCoordenadasUc(codigoUc);
    } else {
      setMarkerPosition(coordenadaSalva);
      setCoordenadasLatLong(coordenadaSalva);
      setValue("CoordenadaX", coordenadaSalva.lng);
      setValue("CoordenadaY", coordenadaSalva.lat);
    }
  }, [codigoUc, coordenadaSalva, updateKey]);

  useEffect(() => {
    if (markerPosition) {
      onChangeCoordinates(markerPosition);
    }
  }, [markerPosition, onChangeCoordinates]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsMarkerLoaded(true);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Snackbar open={openError} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
      {!window.google ? (
        <InputLabel shrink sx={{ color: "#b8b8b8" }}>Carregando mapa...</InputLabel>
      ) :
        <>
          {!hiddenCamposNaoAutorizados && (
            <div style={{ display: "flex", alignItems: "center" }}>
              <InputLabel sx={{ color: "#b8b8b8", fontSize: "12px", fontWeight: 600, marginRight: "10px" }}>Informe aqui a localização no mapa</InputLabel>
              <Tooltip title="Necessário preencher o endereço conforme o exemplo para maior assertividade do mapa." arrow>
                <div style={{ color: yellow[700], cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faCircleExclamation} />
                </div>
              </Tooltip>
            </div>
          )}
          {!hiddenCamposNaoAutorizados && (
            <div style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
              <WhiteInput
                type="text"
                placeholder="Ex: Rua Visc. de Cairú, 630, Criciúma - SC"
                className='search-input'
                disabled={disabled}
                style={{
                  flex: 1,
                  color: "white",
                  fontSize: "16px",
                  width: "340px"
                }}
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
              <BlueHoverButton
                onClick={handleSearch}
                disabled={disabled}
                style={{ background: "#8166F5", color: "white", height: "45px", borderRadius: "0px 10px 10px 0px", border: "none", borderLeft: "none", padding: "0px", minWidth: "44px",  }}
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </BlueHoverButton>
            </div>
          )}
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={coordenadasLatLong}
            zoom={10}
            onClick={handleMapClick}
            options={{
              fullscreenControl: false
            }}
          >
            {isMarkerLoaded && <Marker position={markerPosition} />}
          </GoogleMap>
        </>
      }
      <div style={{ display: "flex" }}>
        <Controller
          name="CoordenadaY"
          control={control}
          render={({ field }) => {
            const formattedValue = formatarCoordenadasGMS(markerPosition.lat, true);
            return (
              <FormControl
                variant="outlined"
                style={{ zIndex: 2, width: "180px", marginTop: "20px", marginRight: "20px" }}
              >
                <InputLabel htmlFor="solicitacao-CoordenadaY" shrink sx={{ color: "#b8b8b8" }}>
                  Latitude
                </InputLabel>
                <WhiteInput
                  fullWidth
                  placeholder="Latitude"
                  id="solicitacao-CoordenadaY"
                  name="CoordenadaY"
                  readOnly={true}
                  {...field}
                  value={formattedValue}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value)) {
                      handleCoordinateChange("lat", value);
                    }
                  }}
                  style={{
                    borderRadius: "10px",
                    borderBottom: errors?.CoordenadaY ? "2.5px solid #e03528" : "1px solid #ccc",
                  }}
                />
                {errors?.CoordenadaY && <HelperText text={errors?.CoordenadaY?.message} />}
              </FormControl>
            );
          }}
        />
        <Controller
          name="CoordenadaX"
          control={control}
          render={({ field }) => {
            const formattedValue = formatarCoordenadasGMS(markerPosition.lng, false);
            return (
              <FormControl
                variant="outlined"
                style={{ zIndex: 2, width: "180px", marginTop: "20px" }}
              >
                <InputLabel htmlFor="solicitacao-CoordenadaX" shrink sx={{ color: "#b8b8b8" }}>
                  Longitude
                </InputLabel>
                <WhiteInput
                  fullWidth
                  placeholder="Longitude"
                  id="solicitacao-CoordenadaX"
                  name="CoordenadaX"
                  readOnly={true}
                  {...field}
                  value={formattedValue}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value)) {
                      handleCoordinateChange("lng", value);
                    }
                  }}
                  style={{
                    borderRadius: "10px",
                    borderBottom: errors?.CoordenadaX ? "2.5px solid #e03528" : "1px solid #ccc",
                  }}
                />
                {errors?.CoordenadaX && <HelperText text={errors?.CoordenadaX?.message} />}
              </FormControl>
            );
          }}
        />

      </div>

    </div>
  );
};

export default Maps;
